import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { Link2, X } from 'lucide-react';
import { captureElementScreenshot } from '../../utils/screenshotCapture';
import ChartInfoButton from './ChartInfoButton';
import ClubDashboardShortkeyButton from './ClubDashboardShortkeyButton';
import FavoriteButton from './FavoriteButton';
import RelatedLinksButton from './RelatedLinksButton';
import ScreenshotButton from './ScreenshotButton';

export interface ChartRelatedLink {
  label: string;
  href: string;
}

interface ChartControlsProps {
  componentId: string;
  componentTitle: string;
  componentType: 'chart' | 'kpi' | 'table' | 'mixed';
  dashboardSource: string;
  onInfo: () => void;
  onFavorite?: () => void;
  isFavorited?: boolean;
  className?: string;
  size?: 'sm' | 'md' | 'lg';
  chartRef?: React.RefObject<HTMLElement>;
  onScreenshot?: (dataUrl: string) => void;
  /** محتوای چپ toolbar — مثلاً badge اعتماد */
  leadingSlot?: React.ReactNode;
  /**
   * @deprecated Prefer `relatedLinks` modal. Kept for non–Market Pulse callers.
   */
  showClubShortkey?: boolean;
  clubShortkeyLabel?: string;
  /** لینک‌های مرتبط — دکمه مودال کوچک باز می‌کند */
  relatedLinks?: ChartRelatedLink[];
  relatedLinksTitle?: string;
}

const resolveRelatedHref = (href: string): string => {
  if (href.includes('?') || href.startsWith('http')) return href;
  if (typeof window === 'undefined') return href;
  return `${href}${window.location.search}`;
};

const ChartControls: React.FC<ChartControlsProps> = ({
  componentId,
  componentTitle,
  onInfo,
  onFavorite,
  isFavorited = false,
  className = '',
  size = 'md',
  chartRef,
  onScreenshot,
  leadingSlot,
  showClubShortkey = false,
  clubShortkeyLabel,
  relatedLinks,
  relatedLinksTitle = 'لینک‌های مرتبط',
}) => {
  const [isRelatedLinksOpen, setIsRelatedLinksOpen] = useState(false);
  const hasRelatedLinks = Boolean(relatedLinks && relatedLinks.length > 0);
  const hasTrailingSlot = Boolean(leadingSlot || showClubShortkey);

  useEffect(() => {
    if (!isRelatedLinksOpen) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsRelatedLinksOpen(false);
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isRelatedLinksOpen]);

  return (
    <>
      <div
        className={`flex items-center gap-2 ${
          hasTrailingSlot ? 'w-full justify-between' : ''
        } ${className} chart-control`}
      >
        <div className="flex items-center gap-2 shrink-0 relative z-10">
          <ScreenshotButton
            onClick={async () => {
              if (chartRef?.current) {
                try {
                  await captureElementScreenshot(chartRef.current, {
                    fileName: `${componentTitle || 'chart'}.png`,
                    onCapture: onScreenshot,
                  });
                } catch (error) {
                  console.error('Screenshot failed:', error);
                  alert('Failed to take screenshot. Please try again.');
                }
              } else {
                alert('Screenshot failed: missing chart reference.');
              }
            }}
            size={size}
            className=""
          />
          <ChartInfoButton onClick={onInfo} size={size} className="" />
          {onFavorite && (
            <FavoriteButton
              onClick={onFavorite}
              isFavorited={isFavorited}
              componentId={componentId}
              size={size}
            />
          )}
          {hasRelatedLinks ? (
            <RelatedLinksButton
              onClick={() => setIsRelatedLinksOpen(true)}
              size={size}
              label={relatedLinksTitle}
            />
          ) : null}
        </div>
        {hasTrailingSlot ? (
          <div className="flex items-center gap-2 min-w-0 ps-2">
            {showClubShortkey && !hasRelatedLinks ? (
              <ClubDashboardShortkeyButton label={clubShortkeyLabel} size={size} />
            ) : null}
            {leadingSlot}
          </div>
        ) : null}
      </div>

      {isRelatedLinksOpen && hasRelatedLinks
        ? ReactDOM.createPortal(
            <>
              <div
                className="fixed inset-0 bg-black/40 transition-opacity"
                style={{ zIndex: 1000000 }}
                onClick={() => setIsRelatedLinksOpen(false)}
                aria-hidden="true"
              />
              <div
                className="fixed inset-0 flex items-center justify-center p-4"
                style={{ zIndex: 1000001 }}
                role="dialog"
                aria-modal="true"
                aria-label={relatedLinksTitle}
              >
                <div
                  className="relative w-full max-w-sm bg-bg-card shadow-xl border border-border-primary text-right animate-fadeIn"
                  style={{ borderRadius: 'var(--card-border-radius)' }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-border-primary">
                    <h2 className="m-0 text-sm font-bold text-text-primary">{relatedLinksTitle}</h2>
                    <button
                      type="button"
                      onClick={() => setIsRelatedLinksOpen(false)}
                      className="rounded-full w-9 h-9 inline-flex items-center justify-center hover:bg-red-500/15 transition-colors focus:outline-none focus:ring-2 focus:ring-red-500"
                      aria-label="بستن"
                    >
                      <X size={18} className="text-red-500" />
                    </button>
                  </div>
                  <ul className="m-0 p-3 list-none space-y-1.5">
                    {relatedLinks!.map((link) => (
                      <li key={`${link.href}-${link.label}`}>
                        <a
                          href={resolveRelatedHref(link.href)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center justify-between gap-2 w-full px-3 py-2.5 rounded-lg text-sm font-medium text-text-primary bg-bg-secondary hover:bg-bg-tertiary hover:text-accent-purple transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-purple"
                          onClick={() => setIsRelatedLinksOpen(false)}
                        >
                          <span>{link.label}</span>
                          <Link2 size={14} className="shrink-0 opacity-60" aria-hidden="true" />
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </>,
            document.body,
          )
        : null}
    </>
  );
};

export default ChartControls;
